import React from "react";

// ─── Per-persona design tokens ─────────────────────────────────────────────
const PERSONA_THEMES = {
  hustler: {
    gradient: "linear-gradient(145deg, #11D99A 0%, #0ca678 60%, #089468 100%)",
    accentGlow: "rgba(17, 217, 154, 0.35)",
    accent: "#0ca678",
    lightBg: "rgba(17, 217, 154, 0.07)",
    chipBorder: "rgba(12, 166, 120, 0.25)",
    patternColor: "rgba(255,255,255,0.06)",
  },
  builder: {
    gradient: "linear-gradient(145deg, #1E293B 0%, #0F172A 60%, #020617 100%)",
    accentGlow: "rgba(99, 102, 241, 0.35)",
    accent: "#6366F1",
    lightBg: "rgba(99, 102, 241, 0.07)",
    chipBorder: "rgba(99, 102, 241, 0.25)",
    patternColor: "rgba(255,255,255,0.04)",
  },
  connector: {
    gradient: "linear-gradient(145deg, #7C3AED 0%, #6D28D9 60%, #5B21B6 100%)",
    accentGlow: "rgba(124, 58, 237, 0.35)",
    accent: "#7C3AED",
    lightBg: "rgba(124, 58, 237, 0.07)",
    chipBorder: "rgba(124, 58, 237, 0.25)",
    patternColor: "rgba(255,255,255,0.05)",
  },
  analyst: {
    gradient: "linear-gradient(145deg, #0EA5E9 0%, #0284C7 60%, #0369A1 100%)",
    accentGlow: "rgba(14, 165, 233, 0.35)",
    accent: "#0284C7",
    lightBg: "rgba(14, 165, 233, 0.07)",
    chipBorder: "rgba(14, 165, 233, 0.25)",
    patternColor: "rgba(255,255,255,0.05)",
  },
  creator: {
    gradient: "linear-gradient(145deg, #F472B6 0%, #EC4899 60%, #DB2777 100%)",
    accentGlow: "rgba(244, 114, 182, 0.35)",
    accent: "#DB2777",
    lightBg: "rgba(244, 114, 182, 0.07)",
    chipBorder: "rgba(244, 114, 182, 0.25)",
    patternColor: "rgba(255,255,255,0.06)",
  },
};

export default function LearningIdentityCard({ identity, learnerName = "Learner" }) {
  if (!identity) return null;

  const theme = PERSONA_THEMES[identity.key] || PERSONA_THEMES.hustler;

  return (
    <div
      id="learning-identity-card"
      style={{
        background: "#ffffff",
        borderRadius: 20,
        overflow: "hidden",
        maxWidth: 480,
        width: "100%",
        margin: "0 auto",
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        boxShadow: `0 0 0 1px rgba(0,0,0,0.06), 0 20px 40px rgba(15,23,42,0.1)`,
      }}
    >
      {/* ── Banner ───────────────────────────────────────────────────────── */}
      <div
        style={{
          background: theme.gradient,
          padding: "32px 28px 28px",
          position: "relative",
          overflow: "hidden",
          textAlign: "center",
        }}
      >
        {/* Decorative dot pattern */}
        <svg
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0.5 }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.2" fill={theme.patternColor} />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>

        {/* Powered-by pill */}
        <div
          style={{
            display: "inline-block",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(8px)",
            borderRadius: 100,
            padding: "3px 12px",
            fontSize: 10,
            fontWeight: 700,
            color: "rgba(255,255,255,0.9)",
            letterSpacing: "1.2px",
            textTransform: "uppercase",
            marginBottom: 18,
            border: "1px solid rgba(255,255,255,0.2)",
            position: "relative",
            zIndex: 1,
          }}
        >
          Moji · Motivar Learning Identity
        </div>

        {/* Badge / Emoji */}
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.18)",
            backdropFilter: "blur(12px)",
            border: "2px solid rgba(255,255,255,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "2.5rem",
            margin: "0 auto 14px",
            boxShadow: `0 8px 24px rgba(0,0,0,0.2), 0 0 0 6px ${theme.accentGlow}`,
            position: "relative",
            zIndex: 1,
          }}
        >
          {identity.badge}
        </div>

        {/* Learner name */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "rgba(255,255,255,0.7)",
            textTransform: "uppercase",
            letterSpacing: "1px",
            marginBottom: 4,
            position: "relative",
            zIndex: 1,
          }}
        >
          {learnerName}'s Learning Identity
        </div>

        {/* Persona Title */}
        <h2
          style={{
            fontSize: "2rem",
            fontWeight: 800,
            color: "#ffffff",
            margin: "0 0 6px",
            letterSpacing: "-0.5px",
            lineHeight: 1.1,
            position: "relative",
            zIndex: 1,
          }}
        >
          {identity.title}
        </h2>

        {/* Subtitle */}
        <div
          style={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "rgba(255,255,255,0.8)",
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {identity.subtitle}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div style={{ padding: "24px 24px 20px" }}>

        {/* Tagline quote */}
        <div
          style={{
            background: theme.lightBg,
            border: `1px solid ${theme.chipBorder}`,
            borderLeft: `4px solid ${theme.accent}`,
            borderRadius: "0 12px 12px 0",
            padding: "14px 16px",
            marginBottom: 20,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "0.93rem",
              fontWeight: 600,
              color: "#1e293b",
              lineHeight: 1.55,
              fontStyle: "italic",
            }}
          >
            "{identity.tagline}"
          </p>
        </div>

        {/* Description */}
        <p
          style={{
            fontSize: "0.84rem",
            color: "#64748b",
            lineHeight: 1.65,
            margin: "0 0 20px",
          }}
        >
          {identity.description}
        </p>

        {/* Career Zones */}
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: theme.lightBg,
                border: `1.5px solid ${theme.chipBorder}`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
              }}
            >
              💼
            </span>
            Career Flourishing Zones
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {identity.careerZones?.map((zone) => (
              <span
                key={zone}
                style={{
                  background: theme.lightBg,
                  color: theme.accent,
                  border: `1px solid ${theme.chipBorder}`,
                  borderRadius: 100,
                  padding: "5px 13px",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                }}
              >
                {zone}
              </span>
            ))}
          </div>
        </div>

        {/* Skill Focus */}
        <div>
          <div
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: theme.lightBg,
                border: `1.5px solid ${theme.chipBorder}`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
              }}
            >
              ⚡
            </span>
            Recommended Skill Focus
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {identity.skillsFocus?.map((skill) => (
              <span
                key={skill}
                style={{
                  background: "#f8fafc",
                  color: "#475569",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: "0.76rem",
                  fontWeight: 500,
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {/* Footer watermark */}
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: "0.7rem", color: "#cbd5e1", fontWeight: 600, letterSpacing: "0.5px" }}>
            POWERED BY MOTIVAR · MOJI AI
          </span>
          <span style={{ fontSize: "0.7rem", color: "#cbd5e1" }}>motivartech.netlify.app</span>
        </div>
      </div>
    </div>
  );
}
